import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { HostBusinessInfoState, MyInfoHost } from 'graphQL/Auth/types';
import Column from 'components/common/Column';
import {
  ICON_EXCLAMATION_CIRCLE_MONO,
  ICON_SETTLEMENT_ALERT,
  ICON_SETTLEMENT_REGIST_REQUEST,
} from 'assets/icons';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import { TaxPayerType } from '../graphQL/types';
import HostInformationModal from './HostInformationModal';
import { INDIVIDUAL_PATH } from 'constants/path';

const RegistHostButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  margin-top: 12px;

  border-radius: 8px;
  border: 1px solid #dbdcdf;
  background: #fff;
  color: #1b1c1e;

  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: -0.6px;
`;

const RequireContainer = styled.div<{ border: string; background: string }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${props => props.border};
  background: ${props => props.background};
`;

const InformationContainer = styled.div`
  display: flex;
  width: 100%;
  border-radius: 12px;
  background: #f7f7f8;
`;

const TaxPayerTypeBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  min-width: 33px;
  height: 23px;
  border-radius: 9999px;
  background: var(--gray-gray-03, #eaebec);
`;

interface SettlementInformationIprops {
  hostInfo: MyInfoHost;
  isRegistRequest: boolean;
  handleSettlementRegistNavigate: () => void;
  handleRegistBankPageNavigate: () => void;
  requestMessage: () => void;
}

const SettlementInformation: React.FC<SettlementInformationIprops> = props => {
  const {
    hostInfo,
    isRegistRequest,
    handleSettlementRegistNavigate,
    handleRegistBankPageNavigate,
    requestMessage,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [isOpenHostInformationModal, setIsOpenHostInformationModal] =
    useState<boolean>(
      location.state && location.state.isOpenHostInformationModal
        ? location.state.isOpenHostInformationModal
        : false,
    );

  const isCheckBusinessInformation =
    hostInfo && hostInfo.settlement?.businessInfo !== null;
  const isCheckRegistState =
    isCheckBusinessInformation &&
    hostInfo.settlement?.businessInfo.status === HostBusinessInfoState.ENROLLED;

  const renderRequireContainer = (): JSX.Element => {
    if (isCheckRegistState) {
      return;
    }

    if (
      hostInfo &&
      hostInfo.settlement?.businessInfo !== null &&
      hostInfo.settlement?.bankAccount !== null
    ) {
      const excludeFieldsForIndividual = [
        'isRegister',
        'businessItem',
        'businessType',
        'representativeName',
      ];
      const excludeFieldsForCorporation = ['isRegister'];

      const isShowRegist = Object.entries(hostInfo.settlement.businessInfo)
        .filter(([key]) => {
          const excludeFields =
            hostInfo.settlement.businessInfo.taxPayerType ===
            TaxPayerType.INDIVIDUAL
              ? excludeFieldsForIndividual
              : excludeFieldsForCorporation;
          return !excludeFields.includes(key);
        })
        .every(([_, value]) => value !== '');

      if (
        hostInfo?.settlement?.businessInfo?.status ===
        HostBusinessInfoState.CANCELED
      ) {
        return (
          <RequireContainer border="#FF8C8C" background="#FEECEC">
            <img
              src={ICON_EXCLAMATION_CIRCLE_MONO}
              alt="calculate require validation"
              style={{ width: '28px', height: '28px' }}
            />
            <Column align="flex-start" gap="12px">
              <Column align="flex-start" gap="4px">
                <Text color="#1b1c1e" size="14px" weight={600}>
                  정산 정보를 다시 인증해야 합니다.
                </Text>
                <Text
                  color="#46474c"
                  size="13px"
                  weight={400}
                  style={{ lineHeight: '138%', letterSpacing: '-0.13px' }}
                >
                  연락처, 호스트 정보, 계좌 정보를 확인하신 후 검수 요청 버튼을
                  눌러주시면, 영업일 기준 3일 이내에 검수가 완료됩니다.
                </Text>
              </Column>
              <Row gap="6px" justifyContent="flex-start">
                <RegistHostButton
                  style={{ marginTop: 0 }}
                  onClick={() => navigate(INDIVIDUAL_PATH)}
                >
                  연락처 확인
                </RegistHostButton>
                <RegistHostButton
                  style={{ marginTop: 0 }}
                  onClick={requestMessage}
                >
                  검수요청
                </RegistHostButton>
              </Row>
            </Column>
          </RequireContainer>
        );
      }

      if (isRegistRequest) {
        return (
          <RequireContainer
            border="rgba(255, 146, 0, 0.40)"
            background="rgba(255, 146, 0, 0.08)"
          >
            <img
              src={ICON_SETTLEMENT_REGIST_REQUEST}
              alt="calculate regist request"
              style={{ width: '28px', height: '28px' }}
            />
            <Column align="flex-start">
              <Text color="#1b1c1e" size="14px" weight={600}>
                제출하신 정보를 검수중에 있어요
              </Text>
              <Text color="#1b1c1e" size="14px" weight={400}>
                제출하신 호스트 정보를 확인하는데 영업일 기준 최대 3일이 소요될
                수 있습니다.
              </Text>
              <RegistHostButton>
                <a href="http://pf.kakao.com/_NQxnQxd" target="_blank">
                  고객센터 문의하기
                </a>
              </RegistHostButton>
            </Column>
          </RequireContainer>
        );
      }

      if (isShowRegist) {
        return (
          <RequireContainer
            border="rgba(255, 146, 0, 0.40)"
            background="rgba(255, 146, 0, 0.08)"
          >
            <img
              src={ICON_SETTLEMENT_REGIST_REQUEST}
              alt="calculate regist request"
            />
            <Column align="flex-start">
              <Column align="flex-start" gap="4px">
                <Text color="#1b1c1e" size="14px" weight={600}>
                  제출하신 정보를 검수 요청해주세요
                </Text>
                <Text color="#46474c" size="14px" weight={400}>
                  제출하신 호스트 정보를 확인 후 검수가 완료되야 정산을 받을 수
                  있어요.
                </Text>
              </Column>
              <RegistHostButton
                onClick={() => {
                  requestMessage();
                }}
              >
                검수 요청
              </RegistHostButton>
            </Column>
          </RequireContainer>
        );
      } else {
        return <></>;
      }
    }

    if (hostInfo.settlement.businessInfo === null) {
      return (
        <RequireContainer
          border="rgba(0, 113, 233, 0.40)"
          background="rgba(0, 113, 233, 0.08)"
        >
          <img src={ICON_SETTLEMENT_ALERT} alt="calculate alert" />
          <Column align="flex-start">
            <Column align="flex-start" gap="4px">
              <Text color="#1b1c1e" size="14px" weight={600}>
                호스트 정보 등록이 필요해요
              </Text>
              <Text color="#46474c" size="14px" weight={400}>
                정산 요청을 위해 먼저 호스트 정보(개인 또는 사업자) 등록이
                필요합니다.
              </Text>
            </Column>
            <RegistHostButton onClick={handleSettlementRegistNavigate}>
              호스트 정보 등록
            </RegistHostButton>
          </Column>
        </RequireContainer>
      );
    }

    if (hostInfo.settlement.bankAccount === null) {
      return (
        <RequireContainer
          border="rgba(0, 113, 233, 0.40)"
          background="rgba(0, 113, 233, 0.08)"
        >
          <img src={ICON_SETTLEMENT_ALERT} alt="calculate alert" />
          <Column align="flex-start">
            <Column align="flex-start" gap="4px">
              <Text color="#1b1c1e" size="14px" weight={600}>
                정산 계좌 등록이 필요해요
              </Text>
              <Text color="#46474c" size="14px" weight={400}>
                판매 내역에 대한 정산 및 대금 입금을 위해 정산 계좌 등록이
                필요합니다.
              </Text>
            </Column>
            <RegistHostButton
              onClick={() => {
                if (hostInfo.settlement.bankAccount === null)
                  handleRegistBankPageNavigate();
              }}
            >
              계좌 정보 등록
            </RegistHostButton>
          </Column>
        </RequireContainer>
      );
    }
  };

  const renderTaxPayerType = (): JSX.Element => {
    return (
      <TaxPayerTypeBox color="#70737C">
        <Text color="#46474c" size="10px" weight={600}>
          {hostInfo.settlement?.businessInfo?.taxPayerType ===
          TaxPayerType.INDIVIDUAL
            ? '개인'
            : hostInfo.settlement?.businessInfo?.taxPayerType ===
              TaxPayerType.INDIVIDUAL_BUSINESS
            ? '개인사업자'
            : '법인사업자'}
        </Text>
      </TaxPayerTypeBox>
    );
  };

  const renderBankInformation = (): JSX.Element => {
    if (hostInfo.settlement.bankAccount !== null) {
      return (
        <Row style={{ width: '100%' }}>
          <Column align="flex-start" gap="10px">
            <Row gap="12px">
              <Text color="#1b1c1e" size="14px" weight={400}>
                {hostInfo.settlement?.bankAccount.account.bank.name}
              </Text>
              {renderTaxPayerType()}
            </Row>
            <Text color="#989BA2" size="13px" weight={400}>
              {hostInfo.settlement?.bankAccount.account.holder},{' '}
              {hostInfo.settlement?.bankAccount.account.accountNo.replace(
                /^(\d{9})(\d{4})$/,
                '*********$2',
              )}
            </Text>
          </Column>
          <RegistHostButton
            style={{ minWidth: '55px', marginTop: 0 }}
            onClick={handleRegistBankPageNavigate}
          >
            수정
          </RegistHostButton>
        </Row>
      );
    }

    return (
      <Text color="#1b1c1e" size="13px">
        등록된 정산 계좌가 없어요
      </Text>
    );
  };

  const renderHostInformation = (): JSX.Element => {
    if (hostInfo.settlement.businessInfo !== null) {
      return (
        <Column gap="12px" align="flex-start">
          <Row style={{ width: '100%' }}>
            <Column gap="8px" align="flex-start">
              <Row gap="12px">
                <Text color="#1B1C1E" size="14px" weight={400}>
                  {hostInfo.settlement.businessInfo.taxPayerName}
                </Text>
                {renderTaxPayerType()}
              </Row>
              <Text color="#989BA2" size="13px" weight={400}>
                {hostInfo.settlement.businessInfo.address}
              </Text>
            </Column>
            <RegistHostButton
              style={{ minWidth: '55px', marginTop: 0 }}
              onClick={() => setIsOpenHostInformationModal(true)}
            >
              {isCheckRegistState ? '보기' : '수정'}
            </RegistHostButton>
          </Row>
          <Text color="#FF4242" size="11px" weight={400}>
            호스트 정보는 검수 완료 시 수정이 불가능해요.
          </Text>
        </Column>
      );
    }

    return (
      <Text color="#1b1c1e" size="13px">
        등록된 호스트 정보가 없어요
      </Text>
    );
  };

  return (
    <React.Fragment>
      <Column gap="32px">
        {renderRequireContainer()}
        <Column gap="16px" align="flex-start">
          <Text size="20px" color="#1b1c1e">
            계좌 정보
          </Text>
          <InformationContainer
            style={{
              padding:
                hostInfo.settlement.bankAccount !== null ? '16px' : '24px 16px',
            }}
          >
            {renderBankInformation()}
          </InformationContainer>
        </Column>
        <Column gap="16px" align="flex-start">
          <Text size="20px" color="#1b1c1e">
            호스트 정보
          </Text>
          <InformationContainer
            style={{
              padding:
                hostInfo.settlement.businessInfo !== null
                  ? '16px'
                  : '24px 16px',
            }}
          >
            {renderHostInformation()}
          </InformationContainer>
        </Column>
      </Column>
      {isOpenHostInformationModal && (
        <HostInformationModal
          host={hostInfo}
          onClose={() => {
            setIsOpenHostInformationModal(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

export default SettlementInformation;
